import styled, { css } from "styled-components";
import { borderRadius, rawColors } from "@magicbi/ui-library";
import { borderStyle, borderWidth } from "../../../design-system/tokens/borders";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { DrillDownGlyph, DrillThroughGlyph } from "../ChartPointActionMenu/ChartPointActionMenu.styles";
import { ClearAllButton } from "../BreadcrumbTrail/BreadcrumbTrail.styles";
import type { StoryFilterBarTone } from "./StoryFilterBar.types";

/** GAP: design chip and trigger height 30px; no spacing token (spacing[7] is 28px). */
const CHIP_HEIGHT = "1.875rem";

/** Figma pads both pills 4/10; 10 is off the 4px spacing grid, so it is written in rem. */
const PILL_PADDING = `${spacing[1]} 0.625rem`;

export const Bar = styled.div`
  display: flex;
  align-items: center;
  padding: ${spacing[2]};
  background-color: ${rawColors.grey[100]};
  border: 1px ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
`;

/**
 * Single wrapping flow for the label, every chip (or the empty pill) and the
 * "Add filter" trigger — the trigger is a flex item in this same row rather
 * than pinned to the bar's far end, so it sits immediately after the last
 * chip and wraps onto a new line with them once the row runs out of width.
 */
export const LeadingGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  min-width: 0;
`;

export const Label = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  white-space: nowrap;
`;

/** The labelled groups side by side; a group that runs out of width wraps whole onto the next line. */
export const GroupList = styled(LeadingGroup)`
  gap: ${spacing[2]} ${spacing[6]};
`;

/** One group: its caption, its chips, its add trigger and its clear link, in one wrapping row. */
export const Group = styled(LeadingGroup)`
  flex: 0 1 auto;
`;

/** The group caption, in capitals as the command header draws it. */
export const GroupLabel = styled(Label)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/** The group's "Clear" link: the breadcrumb trail's own clear link, reused. */
export const GroupClearButton = ClearAllButton;

/** Replaces `Label` once at least one filter is applied — same muted colour, icon-only. */
export const FilterIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${rawColors.text.mediumEmphasis};
`;

/**
 * Figma draws the empty-state chip as a greyed status pill at 60% opacity —
 * deliberately recessive, since it is an absence of state rather than a value.
 */
export const EmptyPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${PILL_PADDING};
  background-color: ${rawColors.grey[300]};
  border: 1px ${borderStyle.solid} ${rawColors.grey[400]};
  border-radius: ${borderRadius.full};
  opacity: 0.6;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
  white-space: nowrap;
`;

/**
 * Dashed pill trigger. `$isOpen` deepens the border while the parent's column
 * dropdown is mounted so the anchor stays visibly tied to the popover.
 */
export const AddFilterTrigger = styled.button<{ $isOpen?: boolean; $section?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  flex-shrink: 0;
  padding: ${PILL_PADDING};
  background: ${({ $isOpen }) => ($isOpen ? rawColors.primary[100] : "none")};
  border-width: ${borderWidth[1]};
  border-style: ${borderStyle.dashed};
  border-color: ${({ $isOpen }) =>
    $isOpen ? rawColors.primary[500] : rawColors.primary[200]};
  border-radius: ${borderRadius.full};
  ${typographyMixin("caption", 2)}
  color: ${rawColors.text.interactive};
  cursor: pointer;
  ${({ $section }) =>
    $section
      ? css`
          /* Sectioned bar: "+ Filter" as text, 28h, weight 600 (F09 #2). */
          box-sizing: border-box;
          min-height: ${CHIP_HEIGHT};
          padding: 0 ${spacing[3]};
          gap: ${spacing[1]};
          font-weight: 600;
          color: ${rawColors.primary[500]};
        `
      : ""}
  ${({ $isOpen }) => ($isOpen ? css`color: ${rawColors.primary[500]};` : "")}

  &:hover:not(:disabled) {
    border-color: ${rawColors.primary[500]};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[300]};
    outline-offset: 0.125rem;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/**
 * Applied-filter chip (Figma 6588:209796) — one renders per entry in
 * `appliedFilters` in the same leading-group slot that shows `EmptyPill`
 * when there are none. `$isOpen` deepens the border the same way
 * `AddFilterTrigger` does while this chip's own panel is mounted. Split into
 * a label button (opens the operator/value panel) and a separate remove
 * button so removing one filter doesn't also toggle its panel open —
 * two interactive regions can't nest inside a single `<button>`.
 */
/**
 * Colours of a sectioned bar's three sections: global is the brand violet, cross-filter the info
 * turquoise, drill the peach accent.
 */
export const TONE_COLORS: Record<StoryFilterBarTone, { bg: string; border: string; text: string }> = {
  global: { bg: rawColors.structural.white, border: rawColors.stroke[200], text: rawColors.text.highEmphasis },
  cross: { bg: rawColors.accent.cross.bg, border: rawColors.accent.cross.border, text: rawColors.accent.cross.text },
  drill: { bg: rawColors.accent.drill.bg, border: rawColors.accent.drill.border, text: rawColors.accent.drill.text },
};

/** The global chip is always brand-bordered and tinted (F22 #2), open, saving or at rest. */
export const FilterChip = styled.div<{ $isOpen?: boolean; $isSaving?: boolean; $tone?: StoryFilterBarTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  flex-shrink: 0;
  /* A chip longer than the whole row wraps its own label instead of
     overflowing the bar; shorter chips just move to the next line. */
  max-width: 100%;
  box-sizing: border-box;
  min-height: ${CHIP_HEIGHT};
  padding: 0 ${spacing[3]};
  background-color: ${(props) => {
    const { $tone } = props;
    if ($tone && $tone !== "global") return TONE_COLORS[$tone].bg;
    return rawColors.primary[100];
  }};
  border-width: ${borderWidth[1]};
  border-style: ${borderStyle.solid};
  border-color: ${(props) => {
    const { $tone } = props;
    if ($tone && $tone !== "global") return TONE_COLORS[$tone].border;
    return rawColors.primary[500];
  }};
  border-radius: ${borderRadius.full};

  &:hover {
    border-color: ${(props) => {
      const { $tone } = props;
      if ($tone && $tone !== "global") return TONE_COLORS[$tone].border;
      return rawColors.primary[500];
    }};
  }
`;

/** The global chip's column name before its value, in the quiet text colour. */
export const FilterChipColumn = styled.span`
  color: ${rawColors.text.lowEmphasis};
`;

export const FilterChipButton = styled.button<{ $tone?: StoryFilterBarTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: start;
  background: none;
  border: none;
  padding: 0;
  ${typographyMixin("caption", 2)}
  color: ${({ $tone }) => ($tone && $tone !== "global" ? TONE_COLORS[$tone].text : rawColors.primary[700])};
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[300]};
    outline-offset: 0.125rem;
  }
`;

/** Mirrors `MultiSelectSearch`'s `ChipRemove` — same affordance, reused rather than redefined for this pill family. */
/** Trailing down chevron on an applied chip (F22 "▾"); inherits the chip text colour. */
export const FilterChipCaret = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const FilterChipLeadingIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.875rem;
  height: 0.875rem;
  color: ${rawColors.text.interactive};
`;

/**
 * Drill-down chip glyph — the same Figma `arrow-down-from-line` export the
 * point-action menu (`ChartPointActionMenu`) draws at 16px, scaled to the
 * 12px this chip already uses for its funnel and remove (×) glyphs so all
 * three read at one size. Inherits `FilterChipLeadingIcon`'s interactive
 * colour through the mask's `currentColor`.
 */
export const FilterChipDrillGlyph = styled(DrillDownGlyph)`
  width: 0.75rem;
  height: 0.75rem;
`;

/** Drill-through twin of `FilterChipDrillGlyph` — the menu's `arrow-right-from-line`, same 12px. */
export const FilterChipDrillThroughGlyph = styled(DrillThroughGlyph)`
  width: 0.75rem;
  height: 0.75rem;
`;

export const FilterChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;

  &:hover {
    color: ${rawColors.text.highEmphasis};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[300]};
    outline-offset: 0.125rem;
  }
`;

/** A chip's quiet tag after its label. */
export const FilterChipTag = styled.span`
  flex-shrink: 0;
  ${typographyMixin("caption", 2)}
  color: ${rawColors.text.lowEmphasis};
  white-space: nowrap;
`;

/** A chip's one extra action, an icon button beside the remove (×). */
export const FilterChipAction = styled(FilterChipRemove)`
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/**
 * The pinned bar of the redesign: one 48px row on the page white with a hairline under it, so the
 * sections read as parts of one bar. It wraps rather than clips when the row runs out of width.
 */
export const SectionBar = styled.div<{ $pinned?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: 3rem;
  box-sizing: border-box;
  padding: 0 ${spacing[3]};
  background-color: ${rawColors.structural.white};
  border-bottom: 1px ${borderStyle.solid} ${rawColors.stroke[200]};
  /* GAP: design shadow 0 6px 16px -10px rgba(24,16,43,.25) has no shadow token. */
  box-shadow: ${({ $pinned }) => ($pinned ? "0 6px 16px -10px rgba(24, 16, 43, 0.25)" : "none")};
`;

/** One section: its label, chips, add trigger and caption. A divider sets it off from the one before. */
export const Section = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[1.5]};
  min-width: 0;
  padding: ${spacing[1]} ${spacing[3]};

  & + & {
    border-left: 1px ${borderStyle.solid} ${rawColors.stroke[200]};
  }

  &:first-child {
    padding-left: 0;
  }
`;

/** The section label: 10px bold capitals with its icon, in the section's colour. */
export const SectionLabel = styled.span<{ $tone: StoryFilterBarTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  ${typographyMixin("overline", 4)}
  font-weight: 700;
  line-height: 1.4; /* GAP: design 10/14, no line-height token. */
  letter-spacing: 0.08em;
  text-transform: uppercase;
  /* Global label and icon are brand purple (F09 #1); cross and drill keep their tones. */
  color: ${({ $tone }) => ($tone === "global" ? rawColors.primary[500] : TONE_COLORS[$tone].text)};
  white-space: nowrap;
`;

/** Quiet text after a section's chips, e.g. where a cross-filter came from. */
export const SectionCaption = styled.span`
  ${typographyMixin("caption", 4)}
  /* GAP: design 11px has no type token; 0.6875rem written directly. */
  font-size: 0.6875rem;
  color: ${rawColors.text.mediumEmphasis};
  white-space: nowrap;
`;

/** An earlier step of the drill path: plain text that acts as a link. */
export const PathCrumb = styled.button`
  padding: 0;
  border: none;
  background: none;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;

  &:hover {
    color: ${rawColors.text.highEmphasis};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[300]};
    outline-offset: 0.125rem;
    border-radius: ${borderRadius.sm};
  }
`;

export const PathSeparator = styled.span`
  display: inline-flex;
  color: ${rawColors.text.lowEmphasis};
`;

/** The drill path's last step: the level the chart is at now, as a drill-coloured pill. */
export const PathCurrent = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${PILL_PADDING};
  background-color: ${TONE_COLORS.drill.bg};
  border: 1px ${borderStyle.solid} ${TONE_COLORS.drill.border};
  border-radius: ${borderRadius.full};
  ${typographyMixin("caption", 2)}
  min-height: 1.625rem;
  box-sizing: border-box;
  color: ${TONE_COLORS.drill.text};
  white-space: nowrap;
`;

/** The drill section's "Back" link, in the drill colour. */
export const BackButton = styled(GroupClearButton)`
  text-decoration: none;
  font-weight: 600;
  color: ${TONE_COLORS.drill.text};
`;

/** "Clear all" at the far end of the sectioned row. */
export const ClearAllLink = styled(GroupClearButton)`
  ${typographyMixin("caption", 2)}
  color: ${rawColors.text.mediumEmphasis};
  margin-left: auto;
  padding-left: ${spacing[3]};
`;

/** Spinner slot after a chip's label while its filter write is in flight. */
export const FilterChipSaving = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${rawColors.text.interactive};
`;

/** The chip's parts row: its conditions side by side, each a field | operator | value trio. */
export const FilterChipParts = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  gap: ${spacing[1]};
  ${typographyMixin("caption", 2)}
`;

/** One condition's three parts, kept together so a long chip wraps between conditions. */
export const FilterChipCondition = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
`;

/** " · " between conditions. */
export const FilterChipConditionSeparator = styled.span`
  color: ${rawColors.text.lowEmphasis};
`;

/** One clickable part (field, operator or value) with its own hover state; a read-only part is plain text. */
export const FilterChipPart = styled.button<{ $tone?: StoryFilterBarTone; $quiet?: boolean; $isOpen?: boolean }>`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: start;
  background: none;
  border: none;
  padding: 0 ${spacing[1]};
  border-radius: ${borderRadius.sm};
  font: inherit;
  font-weight: 600;
  color: ${({ $tone, $quiet }) =>
    $quiet ? rawColors.text.lowEmphasis : $tone && $tone !== "global" ? TONE_COLORS[$tone].text : rawColors.primary[700]};
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.smooth};
  ${({ $isOpen }) => $isOpen && css`background-color: ${rawColors.primary[200]};`}

  &:hover:not(:disabled) {
    background-color: ${rawColors.primary[200]};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[300]};
    outline-offset: 0.0625rem;
  }

  &:disabled {
    cursor: default;
  }
`;

/** Fades and lifts a popup in; one place so every chip popup opens alike. Reduced motion skips it. */
export const popupEnter = css`
  @keyframes filterPopupEnter {
    from {
      opacity: 0;
      transform: translateY(-0.25rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: filterPopupEnter ${duration.fast} ${easing.smooth};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
